import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { Operation } from 'src/app/_models/operation';
import { Product } from 'src/app/_models/product';
import { ProductType } from 'src/app/_models/product_type';
import { Stats } from 'src/app/_models/stats';
import { MessService } from 'src/app/_services/mess-service.service';
import { OperationsService } from 'src/app/_services/operations.service';
import { ProductTypesService } from 'src/app/_services/product-types.service';
import { ProductsService } from 'src/app/_services/products.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-products-edit',
  templateUrl: './products-edit.component.html',
  styleUrls: ['./products-edit.component.scss'],
  animations: [
    trigger('rowExpansionTrigger', [
      state(
        'void',
        style({
          transform: 'translateX(-10%)',
          opacity: 0,
        })
      ),
      state(
        'active',
        style({
          transform: 'translateX(0)',
          opacity: 1,
        })
      ),
      transition('* <=> *', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
})
export class ProductsEditComponent implements OnInit, OnDestroy {
  file: File;

  loading = true;
  updateProductForm: FormGroup;

  productTypes: ProductType[];

  allOperations: Operation[];

  operations: FormArray;
  suboperations: FormArray;

  productForEdit: Product;

  subscriptions: Subscription[] = [];

  stats: Stats;

  id: number;

  constructor(
    private dragulaService: DragulaService,
    private messService: MessService,
    private productTypesService: ProductTypesService,
    private productsService: ProductsService,
    private operationsService: OperationsService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) {
    dragulaService.createGroup('OPERATIONS', {
      moves: (el, container, handle) => {
        return handle.className === 'handle';
      },
    });
  }

  // * Lifecycles === //
  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];

    this.subscriptions.push(
      this.operationsService.getAllOperations().subscribe((operations) => {
        if (!operations) {
          this.router.navigate(['./../../'], { relativeTo: this.route });
          return;
        }
        this.allOperations = operations;
        this.getData();
      })
    );

    this.subscriptions.push(
      this.productTypesService
        .getAllProductTypes()
        .subscribe((productTypes) => {
          this.productTypes = productTypes;
        })
    );

    // * Create form
    this.updateProductForm = this.fb.group({
      batch: [{ value: '', disabled: true }, Validators.required],
      // enable_add_operations: [''],
      label: ['', Validators.required],
      name: ['', Validators.required],
      product_type_id: ['', Validators.required],
      description: [''],
      // total_estimated_time: [{ value: '', disabled: true }],
      operations: this.fb.array([]),
    });
    this.operations = this.updateProductForm.get('operations') as FormArray;
  }

  ngOnDestroy() {
    this.dragulaService.destroy('OPERATIONS');
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.messService.clearErrors();
  }

  // * Helpers === //
  getData() {
    this.loading = true;
    this.subscriptions.push(
      this.productsService.getOne(this.id).subscribe((res: any) => {
        this.productForEdit = res.data;

        this.populateForm();
        this.loading = false;
      })
    );
  }

  onFileUpload(event) {
    this.file = event.files[0];
    let customFile = new File([this.file], this.file.name, {
      type: this.file.type,
    });
  }

  onDeleteDocument(event, document) {
    console.log(document, event);

    this.confirmationService.confirm({
      target: event.target,
      message: 'Da li ste sigurni da želite da obrišete operaciju?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.subscriptions.push(
          this.productsService.deleteDocument(document.id).subscribe(
            () => {
              this.messService.add({
                severity: 'success',
                title: 'Uspešno brisanje',
                detail: 'Obrisali ste operaciju.',
              });
              this.getData();
            },
            (error) => {
              this.messService.add({
                severity: 'error',
                title: 'Greška',
                detail: 'Došlo je do greške, pokušajte ponovo!',
              });
            }
          )
        );
      },
    });
  }
  private populateForm() {
    // this.subscriptions.push(
    //   this.productsService.updateProductData.subscribe(
    //     (productData: Product) => {
    if (!this.productForEdit) {
      this.router.navigate(['./../../'], { relativeTo: this.route });
      return;
    }

    this.updateProductForm.patchValue({
      batch: `${this.productForEdit.batch}`,
      label: `${this.productForEdit.label}`,
      name: this.productForEdit.name,
      product_type_id: this.productForEdit?.product_type?.id,
      description: this.productForEdit.description,
      // total_estimated_time: this.productForEdit.total_estimated_time,
      // enable_add_operations: this.productForEdit.enable_add_operations,
    });

    this.onGetallStats(1);
    //   }
    // )
    // );

    if (this.productForEdit) {
      this.productForEdit.operations.forEach((operation: Operation, index) => {
        let opPrepTimeSum = 0;
        let opOperationTimeSum = 0;
        let nextOpTimeSum = 0;

        this.operations.push(
          this.fb.group({
            operation_id: [operation.operation_id, Validators.required],
            prep_time: [operation.prep_time],
            operation_time: [operation.operation_time],
            next_op_time: [operation.next_op_time],
            suboperations: this.fb.array([]),
          })
        );

        // If suboperation exist
        if (operation.suboperations.length != 0) {
          const subOperation = this.getOperationsControls.controls[index].get(
            'suboperations'
          ) as FormArray;
          operation.suboperations.forEach((suboperation) => {
            subOperation.push(
              this.fb.group({
                suboperation_id: [
                  suboperation.suboperation_id,
                  Validators.required,
                ],
                prep_time: [suboperation.prep_time],
                operation_time: [suboperation.operation_time],
                next_op_time: [suboperation.next_op_time],
              })
            );
            opPrepTimeSum += suboperation.prep_time;
            opOperationTimeSum += suboperation.operation_time;
            nextOpTimeSum += suboperation.next_op_time;
          });

          const currentOperation = this.getOperationsControls.controls[index];
          currentOperation.patchValue({
            prep_time: `${opPrepTimeSum}`,
            operation_time: `${opOperationTimeSum}`,
            next_op_time: `${nextOpTimeSum}`,
          });
          // currentOperation.get('prep_time').disable();
        }
      });
      // Calculate estimated time
      // this.updateTotalEstimatedTime();
    }
  }

  // * Helpers === //

  // Activites on page change
  onActivitesPageChange(event) {
    this.onGetallStats(event.page + 1);
  }

  private onGetallStats(pageIndex: number) {
    // * Get statistics for product
    // this.subscriptions.push(
    //   this.productsService
    //     .getProductStats(this.productForEdit.id, pageIndex)
    //     .subscribe((stats: Stats) => {
    //       console.log(stats, 'STATISTIKA');
    //       this.stats = stats;
    //       this.loading = false;
    //     })
    // );
  }

  // updateTotalEstimatedTime() {
  //   let totalEstTime = 0;
  //   this.getOperationsControls.value.forEach((operation: Operation) => {
  //     totalEstTime +=
  //       +operation.prep_time +
  //       +operation.operation_time +
  //       +operation.next_op_time;
  //   });
  //   // this.updateProductForm.patchValue({
  //   //   total_estimated_time: totalEstTime,
  //   // });
  // }

  get getOperationsControls(): FormArray {
    return this.updateProductForm.get('operations') as FormArray;
  }

  getOperationsControlsByIndex(index: number): FormGroup {
    return this.getOperationsControls.controls[index] as FormGroup;
  }

  getSuboperationsControls(index: number): any {
    const operation = this.getOperationsControls.controls[index] as FormGroup;
    return operation.get('suboperations') as FormArray;
  }

  updateOperationFields(operation, field: any) {
    field.forEach((fieldName) => {
      const object = {};

      const newValueSum = operation.controls.suboperations.value
        .map((val) => parseInt(val[fieldName]))
        .reduce((acc, current) => {
          return acc + current;
        });

      object[fieldName] = newValueSum;

      operation.patchValue(object);
    });
    // this.updateTotalEstimatedTime();
  }

  // * Add/Create/Remove/Update operations & suboperations from form === //
  createOperation(): FormGroup {
    return this.fb.group({
      operation_id: ['', Validators.required],
      prep_time: [''],
      operation_time: [''],
      next_op_time: [''],
      suboperations: this.fb.array([]),
    });
  }

  createSuboperation(): FormGroup {
    return this.fb.group({
      suboperation_id: ['', Validators.required],
      prep_time: [''],
      operation_time: [''],
      next_op_time: [''],
    });
  }

  addOperation(): void {
    this.operations.push(this.createOperation());
  }

  removeOperation(operationId: number) {
    this.operations.removeAt(operationId);
    // this.updateTotalEstimatedTime();
  }

  removeSuboperation(suboperationIndex: number, operationIndex: number) {
    const subOperation = this.getOperationsControls.controls[
      operationIndex
    ].get('suboperations') as FormArray;

    const parentOperation = this.getOperationsControls.controls[operationIndex];
    let parentPrepTime = +parentOperation.get('prep_time').value;
    let parentOperationTime = +parentOperation.get('operation_time').value;
    let parentNextOpTimeSum = +parentOperation.get('next_op_time').value;
    parentOperation.patchValue({
      prep_time:
        parentPrepTime -
        subOperation.controls[suboperationIndex].get('prep_time').value,
      operation_time:
        parentOperationTime -
        subOperation.controls[suboperationIndex].get('operation_time').value,
      next_op_time:
        parentNextOpTimeSum -
        subOperation.controls[suboperationIndex].get('next_op_time').value,
    });

    subOperation.removeAt(suboperationIndex);
    // this.updateTotalEstimatedTime();
  }

  addSuboperation(index: number): void {
    const subOperation = this.getOperationsControls.controls[index].get(
      'suboperations'
    ) as FormArray;

    subOperation.push(this.createSuboperation());
    // this.updateTotalEstimatedTime();
  }

  onChangeOperation(event, formGroupIndex) {
    const selectedOperationId = event.value;
    const subOperation = this.getOperationsControls.controls[
      formGroupIndex
    ].get('suboperations') as FormArray;
    subOperation.clear(); // Clear all subOperations if you change operation

    this.allOperations.filter((operation) => {
      if (operation.id === selectedOperationId) {
        const fGroup = this.operations.at(formGroupIndex);
        fGroup.patchValue({
          prep_time: `${operation.prep_time}`,
          operation_time: `${operation.operation_time}`,
          next_op_time: `${operation.next_op_time}`,
        });
      }
    });
    // this.updateTotalEstimatedTime();
  }

  onChangeSuboperation(
    event,
    operationId,
    formGroupIndex,
    subOpIndex,
    operation: FormGroup,
    fields: string[]
  ) {
    const selectedSuboperationId = event.value;
    const allSubOps = this.findOperation(operationId);

    allSubOps.filter((suboperation) => {
      if (suboperation.id === selectedSuboperationId) {
        const fGroup =
          this.getSuboperationsControls(formGroupIndex).controls[subOpIndex];
        fGroup.patchValue({
          prep_time: suboperation.prep_time ? `${suboperation.prep_time}` : 0,
          operation_time: suboperation.operation_time
            ? `${suboperation.operation_time}`
            : 0,
          next_op_time: suboperation.next_op_time
            ? `${suboperation.next_op_time}`
            : 0,
        });
      }
    });

    this.updateOperationFields(operation, fields);
    // this.updateTotalEstimatedTime();
  }

  findOperation(operationId) {
    let subOp;
    this.allOperations.filter((operation) => {
      if (operation.id === operationId) {
        subOp = operation.suboperations;
      }
    });
    return subOp;
  }

  // * Update product === //
  onUpdateProduct() {
    const product = this.updateProductForm.getRawValue();

    /**
     * Upload file
     */
    const fd = new FormData();

    fd.append('file', this.file);
    fd.append('data', JSON.stringify(product));

    this.subscriptions.push(
      this.productsService.updateProduct(fd, this.productForEdit.id).subscribe(
        () => {
          this.messService.add({
            severity: 'success',
            title: 'Uspešno ažuriranje',
            detail: 'Ažurirali ste proizvod.',
          });
          this.router.navigate(['./../../'], { relativeTo: this.route });
        },
        (error) => {
          const errorObj = error.error.errors;
          const indexOfInvalidField = error.error.item - 1;
          let form;

          if (indexOfInvalidField < 0) {
            form = this.updateProductForm;
          } else {
            form = this.getOperationsControls.controls[indexOfInvalidField];
          }
          const messService = this.messService;
          Object.keys(errorObj).forEach(function (key) {
            const formControl = form.get(key);
            if (formControl) {
              // If error exist set red border to field
              formControl.setErrors({});
            }
            // Push toast error for every field
            messService.addError({
              severity: 'error',
              title: 'Greška',
              detail: errorObj[key],
              sticky: true,
            });
          });
        }
      )
    );
  }
}
